import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import AdminPasswordAPIs from '../../utilities/api/AdminPasswordAPIs';
import background from '../../utilities/dummy_assets/background.png';
import mail from '../../utilities/dummy_assets/mail.gif';
import Logos from '../../components/logos';
import PageTransition from '../../components/PageTransition/PageTransition';

const typographyStyle = {
  color: 'var(--Primary-Primary03, #5C5C5C)',
  fontFamily: 'Inter',
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  letterSpacing: '-0.01375rem',
  marginBottom: '0.25rem',
};

const validation_schema = Yup.object({
  new_password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character'
    )
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const boxStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '.6rem',
  alignSelf: 'stretch',
};

const styledTextField = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: '1.5px solid #B5B5B5', // Set custom border
    },
    '&:hover fieldset': {
      borderColor: '#007BFF', // Border color on hover
    },
    '&.Mui-focused fieldset': {
      borderColor: '#007BFF', // Border color when focused
    },
  },
};

const RestPassword = () => {
  // State management
  const [passwordReset, setPasswordReset] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // Navigation and location hooks
  const navigate = useNavigate();
  const location = useLocation();

  // URL parameters
  const token = new URLSearchParams(location.search).get('token');
  const email = new URLSearchParams(location.search).get('email');

  // Get data from navigation state (for first login scenario)
  const navigationState = location.state;

  // Formik hook - MUST be called before any conditional returns
  const formik = useFormik({
    initialValues: {
      new_password: '',
      confirm_password: '',
    },
    validationSchema: validation_schema,
    onSubmit: values => {
      // Check if this is first login from navigation state
      const isFirstLogin = navigationState && navigationState.isFirstLogin;

      // Prepare data based on scenario
      const data = isFirstLogin
        ? {
            // email: navigationState.email,
            new_password: values.new_password,
            user_id: navigationState.user_id,
          }
        : {
            token: token,
            new_password: values.new_password,
          };
      if (isFirstLogin) {
        AdminPasswordAPIs.FirstTimeLogin(data)
          .then(res => {
            console.log('First time login API response:', res);
            if (res && (res.message || res.data)) {
              setPasswordReset(true);
              toast.success(
                'Password set successfully! You can now login with your new password.'
              );
            }
          })
          .catch(error => {
            console.log('First time login API error:', error);
            toast.error(
              'Failed to set password. Please try again or contact support.'
            );
          });
      } else {
        AdminPasswordAPIs.ResetPassword(data)
          .then(res => {
            if (res.status === 200 || res) {
              setPasswordReset(true);

              if (isFirstLogin) {
                toast.success(
                  'Password set successfully! You can now login with your new password.'
                );
              } else {
                toast.success('Password reset successfully!');
              }
            }
          })
          .catch(error => {
            console.log(error);
            if (
              error.response?.status === 400 ||
              error.response?.status === 401
            ) {
              if (isFirstLogin) {
                toast.error(
                  'Failed to set password. Please try again or contact support.'
                );
              } else {
                toast.error(
                  'Invalid or expired reset token. Please request a new password reset.'
                );
                setTimeout(() => navigate('/forgot_password'), 2000);
              }
            } else {
              toast.error('Failed to reset password. Please try again.');
            }
          });
      }
    },
  });

  // Access control - redirect to login if no valid access method
  useEffect(() => {
    // Check if user has valid access (either token in URL or navigation state data)
    const hasTokenAccess = token && token.trim() !== '';
    const hasNavigationStateAccess =
      navigationState && (navigationState.email || navigationState.user_id);

    if (!hasTokenAccess && !hasNavigationStateAccess) {
      toast.error('Access denied. Please login first.');
      setHasAccess(false);
      setIsValidating(false);
      // Use setTimeout to ensure the redirect happens after state updates
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
    } else {
      setHasAccess(true);
      setIsValidating(false);
    }
  }, [token, email, navigationState, navigate]);

  // Password visibility toggle handler
  const handleClickShowPassword = field => {
    if (field === 'new_password') {
      setShowNewPassword(prev => !prev);
    } else if (field === 'confirm_password') {
      setShowConfirmPassword(prev => !prev);
    }
  };

  // Show loading while validating access
  if (isValidating) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: '#F7F7F7',
          backgroundImage: `url(${background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '1.2rem', color: '#666' }}>
          Validating access...
        </Typography>
      </Box>
    );
  }

  // If no access, show nothing (redirect will happen)
  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: '#F7F7F7',
          backgroundImage: `url(${background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '1.2rem', color: '#666' }}>
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  // Password strength validation checks
  const getPasswordStrength = password => {
    const requirements = [
      { test: password.length >= 8, message: 'At least 8 characters' },
      { test: /[a-z]/.test(password), message: 'One lowercase letter' },
      { test: /[A-Z]/.test(password), message: 'One uppercase letter' },
      { test: /[0-9]/.test(password), message: 'One number' },
      {
        test: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        message: 'One special character',
      },
    ];

    const passedRequirements = requirements.filter(req => req.test);
    const failedRequirements = requirements.filter(req => !req.test);

    return {
      isStrong: failedRequirements.length === 0 && password.length > 0,
      passedCount: passedRequirements.length,
      totalCount: requirements.length,
      failedRequirements,
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: '#F7F7F7',
        backgroundImage: `url(${background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* "Go to Student Site" Link in the Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: '44px',
          right: '104px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          component="span"
          onClick={() =>
            (window.location.href = 'https://tinkerspace.mis.mapit.ai/')
          }
          sx={{
            color: 'var(--Accent-Accent-01, #0062CC)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '16px',
            fontWeight: 500,
          }}
        >
          Go to Student Site
        </Typography>
      </Box>

      {/* Centered Container for Form */}
      <PageTransition
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'center', // Center content vertically
          alignItems: 'center', // Center content horizontally
          padding: '1.5rem',
        }}
      >
        {/* Form Section with White Background */}

        <Box
          sx={{
            width: '100%',
            maxWidth: '26rem',
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 600,
              color: 'var(--Primary-Primary01, #000)',
              marginBottom: '1rem',
            }}
          >
            Reset Password
          </Typography>
          {!passwordReset ? (
            <>
              {/* New Password Field */}
              <Box sx={boxStyle}>
                <Typography sx={typographyStyle}>New Password</Typography>
                <TextField
                  type={showNewPassword ? 'text' : 'password'}
                  sx={{
                    ...styledTextField,
                    '& .MuiOutlinedInput-root': {
                      ...styledTextField['& .MuiOutlinedInput-root'],
                      '& fieldset': {
                        borderColor: formik.errors.new_password
                          ? '#FF4D4F'
                          : '#B5B5B5',
                      },
                    },
                  }}
                  name="new_password"
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                  placeholder="Enter new password"
                  variant="outlined"
                  error={!!formik.errors.new_password}
                  helperText={formik.errors.new_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() =>
                            handleClickShowPassword('new_password')
                          }
                          edge="end"
                          sx={{
                            color: 'var(--Accent-Accent-01, #007BFF)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 123, 255, 0.04)',
                            },
                          }}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{
                    style: {
                      marginLeft: 0,
                      color: '#FF4D4F',
                      fontSize: '0.75rem',
                      marginTop: '4px',
                    },
                  }}
                />
              </Box>

              {/* Confirm Password Field */}
              <Box sx={boxStyle}>
                <Typography sx={typographyStyle}>Confirm Password</Typography>
                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  sx={{
                    ...styledTextField,
                    '& .MuiOutlinedInput-root': {
                      ...styledTextField['& .MuiOutlinedInput-root'],
                      '& fieldset': {
                        borderColor: formik.errors.confirm_password
                          ? '#FF4D4F'
                          : '#B5B5B5',
                      },
                    },
                  }}
                  name="confirm_password"
                  value={formik.values.confirm_password}
                  onChange={formik.handleChange}
                  placeholder="Confirm new password"
                  variant="outlined"
                  error={!!formik.errors.confirm_password}
                  helperText={formik.errors.confirm_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() =>
                            handleClickShowPassword('confirm_password')
                          }
                          edge="end"
                          sx={{
                            color: 'var(--Accent-Accent-01, #007BFF)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 123, 255, 0.04)',
                            },
                          }}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{
                    style: {
                      marginLeft: 0,
                      color: '#FF4D4F',
                      fontSize: '0.75rem',
                      marginTop: '4px',
                    },
                  }}
                />
              </Box>

              {/* Password Strength Indicator */}
              {formik.values.new_password && (
                <Box
                  sx={{
                    width: '100%',
                    marginTop: '1rem',
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                  }}
                >
                  {(() => {
                    const strength = getPasswordStrength(
                      formik.values.new_password
                    );

                    if (strength.isStrong) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <CheckCircle
                            sx={{ color: '#28a745', fontSize: '1.2rem' }}
                          />
                          <Typography
                            sx={{
                              color: '#28a745',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                            }}
                          >
                            Strong Password ✓
                          </Typography>
                        </Box>
                      );
                    } else {
                      return (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: '#666',
                              marginBottom: '0.5rem',
                            }}
                          >
                            Password Requirements:
                          </Typography>
                          {strength.failedRequirements.map((req, index) => (
                            <Typography
                              key={index}
                              sx={{
                                color: '#dc3545',
                                fontSize: '0.8rem',
                                marginBottom: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}
                            >
                              <span style={{ color: '#dc3545' }}>✗</span>
                              {req.message}
                            </Typography>
                          ))}
                        </Box>
                      );
                    }
                  })()}
                </Box>
              )}

              {/* Reset Password Button */}
              <Button
                onClick={formik.handleSubmit}
                disabled={
                  !formik.values.new_password ||
                  !formik.values.confirm_password ||
                  !!formik.errors.new_password ||
                  !!formik.errors.confirm_password
                }
                sx={{
                  padding: '1rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  borderRadius: '12px',
                  backgroundColor:
                    !formik.values.new_password ||
                    !formik.values.confirm_password ||
                    !!formik.errors.new_password ||
                    !!formik.errors.confirm_password
                      ? 'rgba(0, 123, 255, 0.5)'
                      : '#007BFF',
                  color: '#FFF',
                  '&:hover': {
                    backgroundColor:
                      !formik.values.new_password ||
                      !formik.values.confirm_password ||
                      !!formik.errors.new_password ||
                      !!formik.errors.confirm_password
                        ? 'rgba(0, 123, 255, 0.5)'
                        : '#0056b3',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#FFF',
                  }}
                >
                  Reset Password
                </Typography>
              </Button>

              {/* Back to Login Link */}
              <Button
                onClick={() => navigate('/login')}
                sx={{
                  padding: '0.5rem 1rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#007BFF',
                    cursor: 'pointer',
                  }}
                >
                  Back to Login
                </Typography>
              </Button>
            </>
          ) : (
            // Success State
            <>
              {/* Success Animation/Image */}
              <img
                src={mail}
                alt="Success Animation"
                style={{
                  width: 120,
                  height: 120,
                  marginBottom: '1.5rem',
                }}
              />

              {/* Success Message */}
              <Typography
                sx={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#28a745',
                  marginBottom: '0.5rem',
                  textAlign: 'center',
                }}
              >
                Password Reset Successfully!
              </Typography>

              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#666',
                  marginBottom: '2.5rem',
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Great! Your password has been updated successfully.
                <br />
                You can now login with your new password.
              </Typography>

              {/* Login Button */}
              <Button
                onClick={() => navigate('/login')}
                sx={{
                  padding: '1.2rem 2rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  borderRadius: '12px',
                  backgroundColor: '#28a745',
                  color: '#FFF',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                  '&:hover': {
                    backgroundColor: '#218838',
                    boxShadow: '0 6px 16px rgba(40, 167, 69, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#FFF',
                  }}
                >
                  Continue to Login
                </Typography>
              </Button>

              {/* Additional Success Elements */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#d4edda',
                  borderRadius: '8px',
                  border: '1px solid #c3e6cb',
                }}
              >
                <CheckCircle sx={{ color: '#28a745', fontSize: '1.2rem' }} />
                <Typography
                  sx={{
                    color: '#155724',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }}
                >
                  Your account is now secure with the new password
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </PageTransition>

      {/* Footer with "Powered by" */}
      <Logos />
    </Box>
  );
};

export default RestPassword;
